import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { API, authHeaders, imgUrl } from "@/lib/api";
import { CATEGORIES } from "@/data/products";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Plus, Pencil, Trash2, Upload } from "lucide-react";
import { toast } from "sonner";

const empty = { name: "", category: "basmati-rice", variety: "", type: "", origin: "", grain: "", moisture: "", broken: "", moq: "", packaging: "", description: "", image: "", active: true };

const AdminProducts = () => {
  const [rows, setRows] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);

  const load = () => {
    axios.get(`${API}/admin/products`, authHeaders())
      .then((r) => setRows(r.data))
      .catch(() => toast.error("Could not load products"));
  };
  useEffect(load, []);

  const on = (k) => (e) => setForm((f) => ({ ...f, [k]: e?.target ? e.target.value : e }));

  const openEdit = (p) => { setForm({ ...empty, ...p }); setEditing(p); };
  const openNew = () => { setForm(empty); setEditing({}); };

  const uploadImage = async (file) => {
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await axios.post(`${API}/admin/upload`, fd, authHeaders());
      setForm((f) => ({ ...f, image: data.url }));
      toast.success("Image uploaded");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Upload failed");
    }
  };

  const save = async () => {
    if (!form.name || !form.category) { toast.error("Name and category are required"); return; }
    setBusy(true);
    try {
      if (editing?.id) {
        await axios.put(`${API}/admin/products/${editing.id}`, form, authHeaders());
        toast.success("Product updated");
      } else {
        await axios.post(`${API}/admin/products`, form, authHeaders());
        toast.success("Product added");
      }
      setEditing(null);
      load();
    } catch (e) {
      toast.error("Save failed");
    } finally { setBusy(false); }
  };

  const toggleActive = async (p) => {
    try {
      await axios.put(`${API}/admin/products/${p.id}`, { ...p, active: !p.active }, authHeaders());
      setRows((rs) => rs.map((r) => (r.id === p.id ? { ...r, active: !p.active } : r)));
    } catch (e) { toast.error("Update failed"); }
  };

  const remove = async (p) => {
    if (!window.confirm(`Delete "${p.name}" permanently?`)) return;
    try {
      await axios.delete(`${API}/admin/products/${p.id}`, authHeaders());
      setRows((rs) => rs.filter((r) => r.id !== p.id));
      toast.success("Product deleted");
    } catch (e) { toast.error("Delete failed"); }
  };

  return (
    <div data-testid="admin-products">
      <div className="flex items-center justify-between mb-4">
        <div className="font-display text-xl text-[#004225]">Products ({rows.length})</div>
        <Button onClick={openNew} data-testid="add-product-btn" className="btn-primary rounded-full text-[11px] uppercase tracking-widest"><Plus className="h-3.5 w-3.5 mr-2" />Add Product</Button>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
        {rows.map((p) => (
          <div key={p.id} className={`bg-white border rounded-sm overflow-hidden flex gap-3 p-3 ${p.active ? "border-[#004225]/10" : "border-red-200 opacity-70"}`} data-testid={`admin-product-${p.id}`}>
            <img src={imgUrl(p.image)} alt={p.name} className="h-20 w-20 object-cover rounded-sm bg-[#F4F4ED]" />
            <div className="flex-1 min-w-0">
              <div className="text-[10px] font-eyebrow text-[#B5942D]">{p.category}</div>
              <div className="font-semibold text-[#004225] text-sm truncate">{p.name}</div>
              <div className="text-[11px] text-[#171717]/60">MOQ: {p.moq || "—"}</div>
              <div className="mt-1.5 flex items-center gap-2">
                <Switch checked={!!p.active} onCheckedChange={() => toggleActive(p)} data-testid={`active-toggle-${p.id}`} />
                <span className="text-[10px] uppercase tracking-widest text-[#171717]/50">{p.active ? "Live" : "Hidden"}</span>
                <button onClick={() => openEdit(p)} data-testid={`edit-${p.id}`} className="ml-auto text-[#004225] hover:text-[#B5942D]"><Pencil className="h-4 w-4" /></button>
                <button onClick={() => remove(p)} data-testid={`delete-${p.id}`} className="text-[#171717]/40 hover:text-red-600"><Trash2 className="h-4 w-4" /></button>
              </div>
            </div>
          </div>
        ))}
      </div>

      <Dialog open={!!editing} onOpenChange={() => setEditing(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="product-editor">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl text-[#004225]">{editing?.id ? "Edit Product" : "Add Product"}</DialogTitle>
            <DialogDescription>Update specifications, MOQ, packaging, image and visibility. Changes go live immediately.</DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="sm:col-span-2"><Label>Name *</Label><Input value={form.name} onChange={on("name")} data-testid="pf-name" /></div>
            <div>
              <Label>Category *</Label>
              <Select value={form.category} onValueChange={on("category")}>
                <SelectTrigger data-testid="pf-category"><SelectValue /></SelectTrigger>
                <SelectContent>{CATEGORIES.map((c) => (<SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>))}</SelectContent>
              </Select>
            </div>
            <div><Label>Variety</Label><Input value={form.variety} onChange={on("variety")} /></div>
            <div><Label>Type / Processing</Label><Input value={form.type} onChange={on("type")} /></div>
            <div><Label>Origin</Label><Input value={form.origin} onChange={on("origin")} /></div>
            <div><Label>Grain / Quality</Label><Input value={form.grain} onChange={on("grain")} /></div>
            <div><Label>Moisture</Label><Input value={form.moisture} onChange={on("moisture")} /></div>
            <div><Label>Broken</Label><Input value={form.broken} onChange={on("broken")} /></div>
            <div><Label>MOQ</Label><Input value={form.moq} onChange={on("moq")} data-testid="pf-moq" /></div>
            <div className="sm:col-span-2"><Label>Packaging</Label><Input value={form.packaging} onChange={on("packaging")} /></div>
            <div className="sm:col-span-2"><Label>Description</Label><Textarea rows={2} value={form.description} onChange={on("description")} placeholder="Short buyer-facing description (optional)" data-testid="pf-description" /></div>
            <div className="sm:col-span-2">
              <Label>Product Image</Label>
              <div className="flex items-center gap-3 mt-1">
                {form.image && <img src={imgUrl(form.image)} alt="" className="h-16 w-16 object-cover rounded-sm bg-[#F4F4ED]" />}
                <Input value={form.image} onChange={on("image")} placeholder="Image URL" className="flex-1" data-testid="pf-image" />
                <Button type="button" variant="outline" onClick={() => fileRef.current?.click()} data-testid="pf-upload-btn"><Upload className="h-4 w-4 mr-1" />Upload</Button>
                <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={(e) => e.target.files[0] && uploadImage(e.target.files[0])} />
              </div>
            </div>
            <div className="flex items-center gap-2 sm:col-span-2">
              <Switch checked={!!form.active} onCheckedChange={(v) => setForm((f) => ({ ...f, active: v }))} data-testid="pf-active" />
              <span className="text-sm text-[#171717]/70">Active (visible on website)</span>
            </div>
            <div className="sm:col-span-2 pt-2">
              <Button onClick={save} disabled={busy} data-testid="pf-save" className="btn-primary rounded-full px-6 uppercase tracking-widest text-[11px]">{busy ? "Saving…" : "Save Product"}</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminProducts;
